import clsx from 'clsx';
import { useAtom } from 'jotai';

import { leftSidebarCollapsedAtom } from '@/state';
import { useRouteInfo } from '@/hooks/use-route-info';
import { Icon } from '@flintmint/web-shared/components/icon';
import { Tooltip } from '@flintmint/web-shared/components/tooltip';

import styles from './sidebar-toggle.module.scss';

const SIDEBAR_TOGGLE_SHORTCUT = 'CommandOrControl+\\';

/**
 * Collapses or expands the left sidebar. The same control is rendered in the sidebar's own header and,
 * while the sidebar is collapsed, as a tab on the navigation rail's edge (see Layout) — so it never has to
 * live in a route's content.
 */
export const SidebarToggle = (props: SidebarToggleProps) => {
  const { className, tooltipPlacement = 'bottom', testId = 'left-sidebar-toggle' } = props;

  const { leftSidebar } = useRouteInfo();
  const [collapsed, setCollapsed] = useAtom(leftSidebarCollapsedAtom);

  if (!leftSidebar?.collapsible) return null;

  const label = collapsed ? 'Expand sidebar' : 'Collapse sidebar';

  return (
    <Tooltip placement={tooltipPlacement} content={label} shortcut={SIDEBAR_TOGGLE_SHORTCUT}>
      <button
        type="button"
        aria-label={label}
        aria-expanded={!collapsed}
        data-testid={testId}
        data-sidebar-toggle
        className={clsx(styles.sidebarToggle, className)}
        onClick={(event) => {
          // A mouse press (detail > 0) drops focus, so Layout does not hand it to the counterpart toggle
          // and pop that toggle's focus tooltip. A keyboard press keeps focus, which Layout then moves to
          // the toggle that stays reachable.
          if (event.detail > 0) event.currentTarget.blur();

          setCollapsed((current) => !current);
        }}
      >
        <Icon size={20} type={collapsed ? 'sidebar-right-01' : 'sidebar-left-01'} />
      </button>
    </Tooltip>
  );
};

interface SidebarToggleProps {
  className?: string;
  tooltipPlacement?: 'bottom' | 'right';
  testId?: string;
}
